import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { PageHeader, StatusBadge, Empty } from "@/components/Common";
import FileManager from "@/components/FileManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Plus, ListChecks, Paperclip, User, ArrowRight, Trash2, ChevronDown, ChevronUp,
} from "lucide-react";

const STATUSES = ["Pending", "In Progress", "Completed"];

export default function Tasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [depts, setDepts] = useState([]);
  const [scope, setScope] = useState("all");
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", assignee_id: "self", due_date: "", department: "" });

  const load = async () => {
    const { data } = await api.get("/tasks", { params: { scope } });
    setTasks(data);
  };
  useEffect(() => { load(); }, [scope]);
  useEffect(() => {
    api.get("/users").then((r) => setEmployees(r.data));
    api.get("/departments").then((r) => setDepts(r.data));
  }, []);

  const create = async () => {
    try {
      await api.post("/tasks", {
        title: form.title, description: form.description,
        assignee_id: form.assignee_id === "self" ? null : form.assignee_id,
        due_date: form.due_date, department: form.department,
      });
      toast.success("Task created");
      setOpen(false);
      setForm({ title: "", description: "", assignee_id: "self", due_date: "", department: "" });
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const setStatus = async (t, status) => {
    await api.put(`/tasks/${t.id}/status`, { status });
    toast.success(status === "Completed" ? "Task completed — creator notified" : `Marked ${status}`);
    load();
  };

  const remove = async (t) => {
    await api.delete(`/tasks/${t.id}`);
    toast.success("Task deleted");
    load();
  };

  return (
    <>
      <PageHeader
        title="Tasks"
        subtitle="Create tasks for yourself, assign to teammates, and track status through completion."
        testId="tasks-page"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm gap-2 font-bold uppercase text-xs tracking-wide" data-testid="create-task-button">
                <Plus className="h-4 w-4" /> New Task
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">Create Task</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div><Label>Title</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-sm" data-testid="task-title-input" /></div>
                <div><Label>Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-sm" rows={3} data-testid="task-description-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Assign to</Label>
                    <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
                      <SelectTrigger className="mt-1 rounded-sm" data-testid="task-assignee-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="self">Myself</SelectItem>
                        {employees.filter((e) => e.id !== user.id).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                      </SelectContent>
                    </Select></div>
                  <div><Label>Due date</Label>
                    <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1 rounded-sm" data-testid="task-due-date-input" /></div>
                </div>
                <div><Label>Department</Label>
                  <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="task-department-select"><SelectValue placeholder="Select department" /></SelectTrigger>
                    <SelectContent>{depts.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <DialogFooter>
                <Button onClick={create} disabled={!form.title} className="rounded-sm" data-testid="save-task-button">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <Tabs value={scope} onValueChange={setScope} className="mb-4">
        <TabsList className="rounded-sm bg-secondary/60 p-1">
          <TabsTrigger value="all" className="rounded-sm data-[state=active]:bg-card" data-testid="tab-all">All</TabsTrigger>
          <TabsTrigger value="assigned" className="rounded-sm data-[state=active]:bg-card" data-testid="tab-assigned">Assigned to me</TabsTrigger>
          <TabsTrigger value="created" className="rounded-sm data-[state=active]:bg-card" data-testid="tab-created">Created by me</TabsTrigger>
        </TabsList>
      </Tabs>

      {tasks.length === 0 ? (
        <Empty icon={ListChecks} title="No tasks here" sub="Create a task to get started." />
      ) : (
        <div className="space-y-3">
          {tasks.map((t) => (
            <div key={t.id} className="border border-border bg-card rounded-sm" data-testid={`task-${t.id}`}>
              <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold">{t.title}</p>
                    <StatusBadge status={t.status} />
                    {t.file_count > 0 && (
                      <span className="text-xs text-muted-foreground flex items-center gap-0.5"><Paperclip className="h-3 w-3" />{t.file_count}</span>
                    )}
                  </div>
                  {t.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{t.description}</p>}
                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><User className="h-3 w-3" /> {t.assignee_name || "Unassigned"}</span>
                    <span>by {t.creator_name}</span>
                    {t.due_date && <span>due {t.due_date}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Select value={t.status} onValueChange={(v) => setStatus(t, v)}>
                    <SelectTrigger className="w-36 rounded-sm h-9" data-testid={`task-status-${t.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button variant="outline" size="icon" className="rounded-sm h-9 w-9" onClick={() => setExpanded(expanded === t.id ? null : t.id)} data-testid={`expand-task-${t.id}`}>
                    {expanded === t.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                  {t.creator_name === user.name && (
                    <Button variant="ghost" size="icon" className="rounded-sm h-9 w-9 text-destructive" onClick={() => remove(t)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
              {expanded === t.id && (
                <div className="border-t border-border p-4 bg-secondary/20">
                  <FileManager entityType="task" entityId={t.id} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
